/**
 * De hulp van Anna Maria bij het lezen: wat er geselecteerd is, de drie
 * hulpmiddelen, en wat Anna Maria zegt.
 *
 *   Vereenvoudig      de alinea makkelijker: een jaar lager en merkbaar korter
 *                     (de server meet het; het niveau per alinea staat in
 *                     `niveaus`, voor de volgende klik, en de leerling ziet het
 *                     niet); de nieuwe versie komt in de tekst zelf, met
 *                     "Makkelijker gemaakt" erboven (stap 27: anders zag je
 *                     niet dat de alinea vervangen was). Lukte het niet
 *                     (`niet_makkelijker` van de server), dan blijft de
 *                     tekst staan en zegt Anna Maria dat
 *   In mijn moedertaal  de alinea vertaald, in het vak van Anna Maria;
 *                     `vertaling` is dan het id van die alinea (voor de knop
 *                     om de vertaling te laten voorlezen), tot Anna Maria iets
 *                     anders zegt
 *   Uitleg            het geselecteerde woord of stuk zin uitgelegd
 *
 * Alle drie werken op de alinea waarin de leerling iets selecteerde. De
 * alinea's zelf staan in het scherm (LeerlingFlow.js); deze hook krijgt ze
 * mee om de vereenvoudigde versie erin te zetten, en de les van de leerling,
 * zodat de server weet op welk niveau hij leest.
 *
 * Wat er geselecteerd is, leest de hook uit de selectie van de browser
 * (selectieInTekst), als de leerling de muis loslaat, ergens op de pagina,
 * of, bij aanraken en het toetsenbord, als de selectie even niet meer
 * verandert. Tot stap 21 telde een selectie alleen als de muis binnen de
 * alinea werd losgelaten: wie iets verder sleepte (in de witruimte tussen
 * twee alinea's, of naast de tekst), zag de selectie blauw in de tekst,
 * maar de app niet; Uitleg bleef uit, en In mijn moedertaal vertaalde de
 * alinea van de vorige keer. Wis maakt de selectie leeg.
 */
import { useEffect, useState } from 'react';
import { leerlingApi as api } from '../../../api/leerling';
import { LAAGSTE_NIVEAU } from '../../../keuzelijsten';

/**
 * Het kopje (**Kop**) van de alinea op het scherm blijft staan als de nieuwe
 * tekst er geen heeft: een los kopje is aan de alinea erna geplakt
 * (alineas.js), en de server kent alleen die alinea.
 */
export function metKopje(oud, nieuw) {
  const kop = (oud || '').match(/^\s*\*\*[^*]+\*\*\s*/);
  return kop && !/^\s*\*\*/.test(nieuw || '') ? `${kop[0].trim()} ${nieuw}` : nieuw;
}

/** Na zoveel milliseconden zonder verandering telt een selectie met aanraken of het toetsenbord. */
const RUST = 600;

const elementVan = (knoop) => (knoop && knoop.nodeType === 1 ? knoop : knoop?.parentElement);

/**
 * Wat er in de tekst geselecteerd is: { tekst, alineaId } van de alinea
 * (data-alinea-id) waarin de selectie begint, of eindigt als hij erbuiten
 * begint; loopt hij over twee alinea's, dan alleen het stuk in die ene. Niets
 * geselecteerd, of niet in de tekst (bijvoorbeeld in het vak van Anna Maria):
 * null.
 */
export function selectieInTekst() {
  const gekozen = window.getSelection ? window.getSelection() : null;
  if (!gekozen || !gekozen.rangeCount || gekozen.isCollapsed) return null;
  const bereik = gekozen.getRangeAt(0);
  const begin = elementVan(bereik.startContainer)?.closest('[data-alinea-id]');
  const eind = elementVan(bereik.endContainer)?.closest('[data-alinea-id]');
  const alinea = begin || eind;
  if (!alinea) return null;
  const stuk = bereik.cloneRange();
  if (alinea === begin && eind !== begin) stuk.setEnd(alinea, alinea.childNodes.length);
  if (alinea !== begin) stuk.setStart(alinea, 0);
  const tekst = stuk.toString().trim();
  return tekst ? { tekst, alineaId: alinea.getAttribute('data-alinea-id') } : null;
}

export const WELKOM = 'Welkom! Ik ben Anna Maria en ik help je graag. Selecteer een woord of zin in de tekst en klik op "Uitleg" als je iets niet begrijpt.';
/** Wie terugkomt in een tekst waar hij al in begon (herladen, opnieuw ingelogd). */
export const WEER_TERUG = 'Fijn dat je er weer bent! Ik help je graag. Selecteer een woord of zin in de tekst en klik op "Uitleg" als je iets niet begrijpt.';

export function useHulp({ gebruiker, leerlingLesId, setAlineas, niveaus, setNiveaus }) {
  const [selectie, setSelectie] = useState('');
  const [alineaId, setAlineaId] = useState(null);
  const [bericht, setBerichtZelf] = useState('');
  const [vertaling, setVertaling] = useState(null);   // de alinea waarvan de vertaling in het vak staat
  const [vertalingNiveau, setVertalingNiveau] = useState(null);   // en van welke tekst daarvan (na Vereenvoudig)
  const [onderVertaling, setOnderVertaling] = useState('');   // een melding onder de vertaling (voorlezen lukte niet)
  const [bezig, setBezig] = useState(false);

  /** Anna Maria zegt iets anders: de vertaling staat er dan niet meer. */
  const setBericht = (tekst) => {
    setVertaling(null);
    setOnderVertaling('');
    setBerichtZelf(tekst);
  };
  /** Een melding over de vertaling (voorlezen lukte niet), eronder: de vertaling zelf blijft staan. */
  const meldBijVertaling = (tekst) => setOnderVertaling(tekst);

  /** Wat er nu in de tekst geselecteerd is, onthouden; is er niets (of niet in de tekst), dan blijft het vorige. */
  const neemSelectie = () => {
    const nieuw = selectieInTekst();
    if (nieuw) {
      setSelectie(nieuw.tekst);
      setAlineaId(nieuw.alineaId);
    }
  };

  /** De selectie wissen: geen geselecteerd stuk en geen alinea meer (de knop Wis). */
  const wis = () => {
    setSelectie('');
    setAlineaId(null);
    window.getSelection?.()?.removeAllRanges?.();
  };

  // De selectie lezen als de leerling de muis loslaat, waar dan ook; met aanraken of het toetsenbord
  // als de selectie even niet meer verandert (zolang de muis ingedrukt is niet: dan sleept hij nog).
  useEffect(() => {
    let ingedrukt = false;
    let wacht = null;
    const straks = (ms) => { clearTimeout(wacht); wacht = setTimeout(neemSelectie, ms); };
    const neer = () => { ingedrukt = true; clearTimeout(wacht); };
    const los = () => { ingedrukt = false; straks(0); };
    const verandert = () => { if (!ingedrukt) straks(RUST); };
    const toets = () => straks(RUST);
    document.addEventListener('mousedown', neer);
    document.addEventListener('mouseup', los);
    document.addEventListener('touchend', toets);
    document.addEventListener('keyup', toets);
    document.addEventListener('selectionchange', verandert);
    return () => {
      clearTimeout(wacht);
      document.removeEventListener('mousedown', neer);
      document.removeEventListener('mouseup', los);
      document.removeEventListener('touchend', toets);
      document.removeEventListener('keyup', toets);
      document.removeEventListener('selectionchange', verandert);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- één keer; neemSelectie gebruikt alleen de setters
  }, []);

  /** Eén vraag aan de server, met "Even denken..." zolang hij loopt. */
  const vraag = async (doe) => {
    setBezig(true);
    try {
      await doe();
    } catch (err) {
      setBericht(`Oeps, er ging iets mis: ${err.message}`);
    } finally {
      setBezig(false);
    }
  };

  const vereenvoudig = () => {
    if (!alineaId) {
      setBericht('Selecteer eerst een stukje tekst dat je wilt vereenvoudigen.');
      return;
    }
    const huidig = niveaus[alineaId];
    if (huidig === LAAGSTE_NIVEAU) {
      setBericht('Deze tekst is al zo eenvoudig mogelijk gemaakt!');
      return;
    }
    return vraag(async () => {
      const uitkomst = await api.vereenvoudig(alineaId, huidig, leerlingLesId);
      if (uitkomst.niet_makkelijker) {
        // Stap 27: het lukte niet (gemeten); de tekst blijft staan, en Anna Maria zegt dat eerlijk.
        setBericht(uitkomst.current_niveau === LAAGSTE_NIVEAU
          ? 'Deze tekst is al zo eenvoudig mogelijk gemaakt!'
          : 'Het lukte me nu niet om dit stuk makkelijker te maken. Probeer het straks nog eens. '
            + 'Begrijp je een woord niet? Selecteer het en klik op "Uitleg".');
        return;
      }
      setAlineas(prev => prev.map(p => (p.id === alineaId ? { ...p, inhoud: metKopje(p.inhoud, uitkomst.inhoud) } : p)));
      if (uitkomst.current_niveau) setNiveaus(prev => ({ ...prev, [alineaId]: uitkomst.current_niveau }));
      // geen niveau: niveaus en scores zijn voor de leerling onzichtbaar (stap 25)
      setBericht(`Ik heb dit stuk makkelijker gemaakt. Het staat nu zo in de tekst:\n\n${uitkomst.inhoud}`);
    });
  };

  const vertaal = () => {
    if (!gebruiker.moedertaal) {
      setBericht('Je hebt nog geen moedertaal ingesteld. Vraag je leerkracht om dit te doen.');
      return;
    }
    if (!alineaId) {
      setBericht('Selecteer eerst een stukje tekst dat je wilt vertalen.');
      return;
    }
    return vraag(async () => {
      // het niveau van wat er nu staat: na Vereenvoudig vertaalt de server die tekst, niet de versie ervoor
      const uitkomst = await api.vertaal(alineaId, leerlingLesId, niveaus[alineaId]);
      setBericht(`Hier is de tekst in het ${gebruiker.moedertaal}:\n\n${uitkomst.translated}`);
      setVertaling(alineaId);
      setVertalingNiveau(niveaus[alineaId] || null);
    });
  };

  const legUit = () => {
    if (!selectie.trim()) {
      setBericht('Selecteer eerst een woord of zin in de tekst die je wilt laten uitleggen.');
      return;
    }
    if (!alineaId) {
      setBericht('Selecteer eerst een woord of zin in de tekst.');
      return;
    }
    return vraag(async () => {
      const uitkomst = await api.legUit(alineaId, selectie, leerlingLesId, niveaus[alineaId]);
      setBericht(`"${selectie}"\n\n${uitkomst.explanation}`);
      setSelectie('');
    });
  };

  return { selectie, alineaId, bericht, setBericht, vertaling, vertalingNiveau, onderVertaling, meldBijVertaling, bezig, wis,
    vereenvoudig, vertaal, legUit, niveaus };
}
